import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { useStateValue } from "../context/StateProvider";

const MAX_RESULTS = 8;

export default function GlobalSearch() {
  const [{ deals }] = useStateValue();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  // Cmd/Ctrl+K focuses the search.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return (deals || [])
      .filter((d) => {
        const addr = (d?.propertyaddress || "").toLowerCase();
        const owner = `${d?.firstName || ""} ${d?.lastName || ""}`
          .trim()
          .toLowerCase();
        return addr.includes(q) || (owner && owner.includes(q));
      })
      .slice(0, MAX_RESULTS);
  }, [query, deals]);

  const go = (deal) => {
    if (!deal) return;
    navigate(`/dashboard/deals-detail/${deal.createdat || deal.id}`);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[activeIdx]);
    } else if (e.key === "Escape") {
      setQuery("");
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={wrapRef} className="relative w-36 sm:w-56 lg:w-72">
      <div className="relative">
        <Search
          size={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIdx(0);
          }}
          onFocus={() => query && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search address…"
          className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-400 rounded-md pl-8 pr-7 py-1.5 text-[13px] text-slate-900 placeholder:text-slate-400 outline-none transition"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setOpen(false);
              inputRef.current?.focus();
            }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
            aria-label="Clear search"
          >
            <X size={13} />
          </button>
        )}
      </div>
      {open && query && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden z-50">
          {results.length === 0 ? (
            <div className="px-3 py-2.5 text-[12.5px] text-slate-500">
              No deals match "{query}"
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {results.map((d, idx) => {
                const stage = d?.dealStage || "—";
                const addr = d?.propertyaddress || "Untitled deal";
                return (
                  <li
                    key={d.createdat || d.id || idx}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      go(d);
                    }}
                    className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-3 ${
                      idx === activeIdx ? "bg-slate-100" : "hover:bg-slate-50"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-slate-900 truncate">
                        {addr}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {d?.firstName || d?.lastName
                          ? `${d?.firstName || ""} ${d?.lastName || ""}`.trim()
                          : d?.dealtype || ""}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 shrink-0">
                      {stage}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
